import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Modal, ModalContent } from '@nextui-org/react';
import { useMediaQuery } from 'react-responsive';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  BellOff,
  Check,
  ChevronRight,
  CircleHelp,
  Clock,
  Download,
  Dumbbell,
  Info,
  Minus,
  Play,
  Plus,
  SkipForward,
  TrendingUp,
  Trophy,
  Volume1,
  Volume2,
  VolumeX,
  X,
} from 'lucide-react';

import '../../styles/pages/guiado.css';
import alexaEcho from '../../assets/alexa-echo-rest.jpg';
import { Burst } from '../app/Burst';
import { formatSeconds } from '../../utils/workoutStats';
import { formatNumber } from '../../utils/guidedSession';
import { isAppleMobile } from '../../utils/device';
import { prefersReducedMotion } from '../../utils/motion';

// The guided workout, direction A · Foco (owner's pick, 29/10/2026): one set at a time, its reps and load in big
// numbers with thumb-sized steppers and one button; the rest takes the whole screen; the how-to is a sheet.

const DESKTOP = '(min-width: 960px)';
const RING_SIZE = 264;
const RING_STROKE = 14;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const session = (t, key, options) => t(`pages.WorkoutExecution.session.${key}`, options);

/**
 * The exercise on its light tile: the video (its thumbnail as poster), else the GIF, else the thumbnail, else a
 * dumbbell. `still` shows only the thumbnail (lists). A video that fails to load falls back to the thumbnail.
 */
const ExerciseArt = ({ entry, still = false, className = '' }) => {
  const [failed, setFailed] = useState(false);
  const { videoUrl, gifUrl, thumbnailUrl } = entry?.exercise || {};
  let content;
  if (!still && videoUrl && !failed) {
    content = <video src={videoUrl} poster={thumbnailUrl || undefined} autoPlay loop muted playsInline onError={() => setFailed(true)} />;
  } else if (!still && gifUrl) {
    content = <img src={gifUrl} alt="" />;
  } else if (thumbnailUrl) {
    content = <img src={thumbnailUrl} alt="" />;
  } else {
    content = <Dumbbell className="mt-guide-art__none" aria-hidden="true" />;
  }
  return <span className={`mt-guide-art ${className}`}>{content}</span>;
};

// The lift's name, and after it a warm-up or feeder entry's kind as a pill, never "(Aquecimento)" inside the name
const EntryName = ({ entry }) => {
  const { t } = useTranslation();
  return (
    <>
      {entry.name}
      {entry.kind && (
        <>
          {' '}
          <span className="mt-guide-pill">{session(t, entry.kind)}</span>
        </>
      )}
    </>
  );
};

const megabytes = (bytes) => Math.round(bytes / 1e6);

// The workout's own voice getting ready (pt), in place of the start button: first the question before a download
// (its size: some people train on mobile data), then the download, then loading it
const VoicePrep = ({ status, progress, downloadSize, onDownload, onSkip }) => {
  const { t } = useTranslation();

  if (status === 'confirm') {
    const size = megabytes(downloadSize);
    return (
      <div className="mt-guide-voice">
        <p className="mt-guide-voice__label">{session(t, 'voiceAskTitle')}</p>
        <p className="mt-guide-voice__note">{session(t, 'voiceAskBody', { size })}</p>
        <button type="button" className="mt-btn mt-btn--primary mt-guide-cta" onClick={onDownload}>
          <Download aria-hidden="true" />
          {session(t, 'voiceDownload', { size })}
        </button>
        <button type="button" className="mt-guide-voice__skip" onClick={onSkip}>
          {session(t, 'voiceSkip')}
        </button>
      </div>
    );
  }

  const isDownloading = status === 'downloading' && progress?.total;
  const percent = isDownloading ? Math.round((progress.loaded / progress.total) * 100) : null;

  return (
    <div className="mt-guide-voice" role="status" aria-live="polite">
      <div className="mt-guide-voice__head">
        <span className="mt-guide-voice__label">{session(t, isDownloading ? 'voiceDownloading' : 'voicePreparing')}</span>
        {isDownloading && (
          <span className="mt-guide-voice__amount">
            {session(t, 'voiceAmount', { loaded: megabytes(progress.loaded), total: megabytes(progress.total) })}
          </span>
        )}
      </div>
      <span
        className={`mt-guide-voice__bar${isDownloading ? '' : ' mt-guide-voice__bar--busy'}`}
        role="progressbar"
        aria-label={session(t, isDownloading ? 'voiceDownloading' : 'voicePreparing')}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent ?? undefined}
      >
        <span style={isDownloading ? { width: `${percent}%` } : undefined} />
      </span>
      {isDownloading && <p className="mt-guide-voice__note">{session(t, 'voiceNote')}</p>}
      <button type="button" className="mt-guide-voice__skip" onClick={onSkip}>
        {session(t, 'voiceSkip')}
      </button>
    </div>
  );
};

/**
 * The opening: the workout at a glance (count of exercises, sets and minutes, then each exercise) and, in thumb
 * reach, the start and the other way to train: with the Alexa. That choice used to be a dialog before every guided
 * workout; here it costs whoever trains on the site no tap at all.
 */
export const SessionIntro = ({
  title,
  description,
  entries,
  setCount,
  minutes,
  onStart,
  onAlexa,
  voice,
  onDownloadVoice,
  onSkipVoice,
  showSilentHint,
}) => {
  const { t } = useTranslation();
  const isDesktop = useMediaQuery({ query: DESKTOP });
  const isVoicePreparing = ['confirm', 'downloading', 'preparing'].includes(voice?.status);
  const first = entries[0];
  // "Começa com o aquecimento de Supino": the kind of the first entry picks the line (i18next context)
  const firstLine = first && session(t, 'firstUp', { name: first.name, context: first.kind || undefined });

  return (
    <div className="mt-guide mt-guide--intro">
      <header className="mt-guide-back">
        <Link className="mt-guide-back__link" to="/workouts">
          <ArrowLeft aria-hidden="true" />
          {session(t, 'back')}
        </Link>
      </header>
      <div className="mt-guide-intro">
        <div className="mt-guide-intro__main">
          <h1 className="mt-guide-intro__title">{title}</h1>
          {description && <p className="mt-guide-intro__muscles">{description}</p>}
          <dl className="mt-guide-stats mt-guide-intro__stats">
            <div>
              <dt>{session(t, 'statExercises', { count: entries.length })}</dt>
              <dd>{entries.length}</dd>
            </div>
            <div>
              <dt>{session(t, 'statSets', { count: setCount })}</dt>
              <dd>{setCount}</dd>
            </div>
            <div>
              <dt>{session(t, 'statMinutes')}</dt>
              <dd>~{minutes}</dd>
            </div>
          </dl>
          <ol className="mt-guide-intro__list">
            {entries.map((entry) => (
              <li key={entry.index} className="mt-guide-intro__row">
                <span className="mt-guide-intro__n" aria-hidden="true">
                  {entry.index + 1}
                </span>
                <ExerciseArt entry={entry} still className="mt-guide-intro__thumb" />
                <span className="mt-guide-intro__text">
                  <span className="mt-guide-intro__name">{entry.name}</span>
                  <span className="mt-guide-intro__dose">
                    {entry.kind ? `${session(t, entry.kind)} · ` : ''}
                    {entry.dose}
                  </span>
                </span>
              </li>
            ))}
          </ol>
        </div>
        <aside className="mt-guide-intro__side" aria-label={session(t, 'startLabel')}>
          {/* Desktop: what the workout opens with, moving */}
          {isDesktop && first && (
            <>
              <ExerciseArt entry={first} className="mt-guide-intro__first" />
              <p className="mt-guide-intro__cap">{firstLine}</p>
            </>
          )}
          <div className="mt-guide-dock mt-guide-dock--intro">
            {isVoicePreparing ? (
              <VoicePrep
                status={voice.status}
                progress={voice.progress}
                downloadSize={voice.downloadSize}
                onDownload={onDownloadVoice}
                onSkip={onSkipVoice}
              />
            ) : (
              <>
                {/* iPhone / iPad: the voice mixes with the person's music, and the silent switch mutes it */}
                {showSilentHint && (
                  <p className="mt-guide-hint">
                    <BellOff aria-hidden="true" />
                    {session(t, 'silentHint')}
                  </p>
                )}
                <button type="button" className="mt-btn mt-btn--primary mt-guide-cta mt-guide-cta--start" onClick={onStart}>
                  <Play aria-hidden="true" />
                  {session(t, 'start')}
                </button>
                <Link className="mt-guide-alexa" to="/sync" onClick={onAlexa}>
                  <img src={alexaEcho} alt="" />
                  <span className="mt-guide-alexa__text">
                    <span className="mt-guide-alexa__title">{session(t, 'alexa')}</span>
                    <span className="mt-guide-alexa__sub">{session(t, 'alexaSub')}</span>
                  </span>
                  <ChevronRight className="mt-guide-alexa__chevron" aria-hidden="true" />
                </Link>
              </>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
};

/**
 * The bar's speaker opens the voice's volume: a slider and mute, so the voice sits right against the person's music.
 * Letting go of the slider plays the current line at the new volume (WorkoutExecution).
 */
const VoiceVolume = ({ level, isMuted, onLevelChange, onToggleMute }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef(null);
  const sliderRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    sliderRef.current?.focus();
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setIsOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  const isSilent = isMuted || level <= 0;
  const BarIcon = isSilent ? VolumeX : level < 0.45 ? Volume1 : Volume2;
  const percent = Math.round(level * 100);

  return (
    <div className="mt-volume" ref={rootRef}>
      <button
        type="button"
        className="mt-guide-icon"
        onClick={() => setIsOpen((open) => !open)}
        aria-label={session(t, 'voiceVolume')}
        aria-expanded={isOpen}
        aria-controls="mt-volume-panel"
      >
        <BarIcon aria-hidden="true" />
      </button>
      {isOpen && (
        <div className="mt-volume__panel" id="mt-volume-panel" role="group" aria-label={session(t, 'voiceVolume')}>
          <p className="mt-volume__title">{session(t, 'voiceVolume')}</p>
          <div className="mt-volume__row">
            <button
              type="button"
              className="mt-volume__mute"
              onClick={onToggleMute}
              aria-label={isMuted ? t('pages.WorkoutExecution.unmute') : t('pages.WorkoutExecution.mute')}
            >
              {isSilent ? <VolumeX aria-hidden="true" /> : <Volume1 aria-hidden="true" />}
            </button>
            <input
              ref={sliderRef}
              type="range"
              className={`mt-volume__slider${isMuted ? ' mt-volume__slider--muted' : ''}`}
              min="0"
              max="100"
              step="5"
              value={percent}
              style={{ '--fill': `${percent}%` }}
              onChange={(event) => onLevelChange(Number(event.target.value) / 100)}
              aria-label={session(t, 'voiceVolume')}
              aria-valuetext={`${percent}%`}
            />
            <Volume2 className="mt-volume__max" aria-hidden="true" />
          </div>
        </div>
      )}
    </div>
  );
};

// The iPhone's side switch going from silent (orange showing) to ring, while the bell on the screen loses its slash and
// rings: what "turn off Silent mode" means, in three seconds. Still for reduced motion (shows the end state).
const SilentSwitchAnimation = () => (
  <svg className="mt-silent-anim" viewBox="0 0 160 120" aria-hidden="true">
    <rect className="mt-silent-anim__phone" x="52" y="8" width="62" height="104" rx="13" />
    <rect className="mt-silent-anim__screen" x="57" y="13" width="52" height="94" rx="9" />
    <rect className="mt-silent-anim__slot" x="45" y="24" width="7" height="16" rx="2.5" />
    <rect className="mt-silent-anim__orange" x="46.5" y="33" width="4" height="5" rx="1" />
    <rect className="mt-silent-anim__knob" x="46" y="32" width="5" height="7" rx="1.5" />
    <g className="mt-silent-anim__bell" transform="translate(71 46)">
      <path d="M12 3a6 6 0 0 0-6 6c0 6-2.5 7.5-2.5 7.5h17S18 15 18 9a6 6 0 0 0-6-6Z" />
      <path d="M10 20a2.2 2.2 0 0 0 4 0" />
      <path className="mt-silent-anim__slash" d="M3 3l18 18" />
      <path className="mt-silent-anim__wave mt-silent-anim__wave--l" d="M1 6.5a9 9 0 0 0 0 7" />
      <path className="mt-silent-anim__wave mt-silent-anim__wave--r" d="M23 6.5a9 9 0 0 1 0 7" />
    </g>
  </svg>
);

/** "No sound?": the help behind the bar's ? button. On an iPhone, the silent switch (and how to flip it) */
export const SoundHelpDialog = ({ isOpen, onClose, isApple = isAppleMobile() }) => {
  const { t } = useTranslation();

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => !open && onClose()}
      placement="center"
      hideCloseButton
      classNames={{ base: 'mt-root mt-dialog mt-sound-help', backdrop: 'mt-dialog__backdrop' }}
    >
      <ModalContent>
        {() => (
          <div className="mt-dialog__body">
            <h2 className="mt-dialog__title">{session(t, 'soundHelpTitle')}</h2>
            {isApple ? (
              <>
                <SilentSwitchAnimation />
                <p className="mt-dialog__text">{session(t, 'soundHelpIntro')}</p>
                <ul className="mt-sound-help__steps">
                  <li>{session(t, 'soundHelpSwitch')}</li>
                  <li>{session(t, 'soundHelpAction')}</li>
                  <li>{session(t, 'soundHelpVolume')}</li>
                </ul>
              </>
            ) : (
              <p className="mt-dialog__text">{session(t, 'soundHelpOther')}</p>
            )}
            <div className="mt-dialog__actions">
              <button type="button" className="mt-btn mt-btn--primary" onClick={onClose}>
                {session(t, 'soundHelpOk')}
              </button>
            </div>
          </div>
        )}
      </ModalContent>
    </Modal>
  );
};

// The sheet rises from the bottom on phones and fades in as a window on desktop. Reduced motion only fades: NextUI's
// own slide stays stuck 80px down there (as in the welcome tour)
const sheetMotion = (isDesktop) => {
  const still = isDesktop || prefersReducedMotion();
  return {
    variants: {
      enter: { opacity: 1, y: 0, scale: 1, transition: { duration: still ? 0.2 : 0.32, ease: [0.16, 1, 0.3, 1] } },
      exit: { opacity: 0, y: still ? 0 : 48, scale: 1, transition: { duration: 0.16, ease: 'easeIn' } },
    },
  };
};

/**
 * How to do an exercise: its video, the muscles it works and the steps, numbered one per line. A sheet on phones;
 * on desktop a window with the video beside the steps.
 */
const HowToSheet = ({ entry, onClose }) => {
  const { t } = useTranslation();
  const isDesktop = useMediaQuery({ query: DESKTOP });
  // Kept while the sheet animates out, after its entry is gone
  const lastRef = useRef(entry);
  if (entry) lastRef.current = entry;
  const shown = entry || lastRef.current;

  return (
    <Modal
      isOpen={!!entry}
      onOpenChange={(open) => !open && onClose()}
      placement={isDesktop ? 'center' : 'bottom'}
      hideCloseButton
      motionProps={sheetMotion(isDesktop)}
      aria-labelledby="mt-guide-howto-title"
      classNames={{ base: 'mt-root mt-guide-howto', backdrop: 'mt-dialog__backdrop' }}
    >
      <ModalContent>
        {() =>
          shown && (
            <div className="mt-guide-howto__inner">
              <span className="mt-guide-howto__grab" aria-hidden="true" />
              <ExerciseArt key={shown.index} entry={shown} className="mt-guide-howto__media" />
              <div className="mt-guide-howto__info">
                <div className="mt-guide-howto__head">
                  <div>
                    <h2 id="mt-guide-howto-title" className="mt-guide-howto__title">
                      <EntryName entry={shown} />
                    </h2>
                    {shown.works && <p className="mt-guide-howto__works">{shown.works}</p>}
                  </div>
                  <button type="button" className="mt-guide-icon" onClick={onClose} aria-label={session(t, 'closeHowTo')}>
                    <X aria-hidden="true" />
                  </button>
                </div>
                {shown.steps.length > 0 && (
                  <ol className="mt-guide-steps">
                    {shown.steps.map((step, index) => (
                      <li key={index}>
                        <span aria-hidden="true">{index + 1}</span>
                        <p>{step}</p>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </div>
          )
        }
      </ModalContent>
    </Modal>
  );
};

/**
 * One of the set's two numbers, big enough to read from the bench, with − and + the size of a thumb. The number
 * also takes typing (a comma for decimals in pt).
 */
const SetStepper = ({ value, unit, label, fieldLabel, lessLabel, moreLabel, inputMode, onChange, onCommit, onStep }) => {
  // Its width in digits: a decimal comma or point is about half a digit wide (47,5 is 3.5, not 4)
  const text = String(value);
  const digits = Math.max(1, text.replace(/[.,]/g, '').length + (text.match(/[.,]/g) || []).length * 0.5);
  return (
    <div className="mt-guide-stepper">
      <span className="mt-guide-stepper__value">
        <input
          className="mt-guide-num"
          type="text"
          inputMode={inputMode}
          enterKeyHint="done"
          autoComplete="off"
          placeholder="0"
          value={value}
          style={{ '--digits': digits }}
          aria-label={fieldLabel}
          onFocus={(event) => event.target.select()}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onCommit}
          onKeyDown={(event) => {
            if (event.key === 'Enter') event.currentTarget.blur();
          }}
        />
        {unit && (
          <span className="mt-guide-stepper__unit" aria-hidden="true">
            {unit}
          </span>
        )}
      </span>
      <span className="mt-guide-stepper__label" aria-hidden="true">
        {label}
      </span>
      <span className="mt-guide-stepper__btns">
        <button type="button" onClick={() => onStep(-1)} aria-label={lessLabel}>
          <Minus aria-hidden="true" />
        </button>
        <button type="button" onClick={() => onStep(1)} aria-label={moreLabel}>
          <Plus aria-hidden="true" />
        </button>
      </span>
    </div>
  );
};

// What comes after the set, over its button: the next set's dose, the next exercise, or the end
const AfterLine = ({ after }) => {
  const { t } = useTranslation();
  if (after.kind === 'last') {
    return (
      <p className="mt-guide-after">
        <Check aria-hidden="true" />
        <span>{session(t, 'lastSet')}</span>
      </p>
    );
  }
  return (
    <p className="mt-guide-after">
      <ChevronRight aria-hidden="true" />
      <span>
        {session(t, 'afterLabel')}{' '}
        <b>{after.kind === 'set' ? session(t, 'afterSet', { n: after.setNumber, dose: after.dose }) : after.entry.name}</b>
      </span>
    </p>
  );
};

const RestStage = ({ timeLeft, total, next, isDesktop, onMinus, onPlus, onSkip, onHowTo, regionRef }) => {
  const { t } = useTranslation();
  const progress = total ? Math.min(timeLeft / total, 1) : 0;
  // Screen readers hear the rest once when it starts and again when it's changed, not every second
  const [announcement, setAnnouncement] = useState(() => session(t, 'restFor', { time: formatSeconds(timeLeft) }));
  const shift = (handler, delta) => {
    handler();
    setAnnouncement(session(t, 'restFor', { time: formatSeconds(Math.max(0, timeLeft + delta)) }));
  };
  const sub = next.sameExercise
    ? session(t, 'restNextSet', { n: next.setNumber, total: next.entry.totalSets, dose: next.dose })
    : session(t, 'restNextExercise', { dose: next.entry.dose });

  return (
    <>
      <section className="mt-guide-rest" aria-labelledby="mt-guide-rest-title" tabIndex={-1} ref={regionRef}>
        <div className="mt-guide-rest__main">
          <div className="mt-guide-ring">
            <svg viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`} aria-hidden="true">
              <circle className="mt-guide-ring__track" cx={RING_SIZE / 2} cy={RING_SIZE / 2} r={RING_RADIUS} fill="none" strokeWidth={RING_STROKE} />
              <circle
                className="mt-guide-ring__arc"
                cx={RING_SIZE / 2}
                cy={RING_SIZE / 2}
                r={RING_RADIUS}
                fill="none"
                strokeWidth={RING_STROKE}
                strokeLinecap="round"
                strokeDasharray={RING_CIRCUMFERENCE}
                strokeDashoffset={RING_CIRCUMFERENCE * (1 - progress)}
              />
            </svg>
            <div className="mt-guide-ring__center">
              <span className="mt-guide-ring__time" role="timer" aria-live="off">
                {formatSeconds(timeLeft)}
              </span>
              <h1 id="mt-guide-rest-title" className="mt-guide-ring__label">
                {session(t, 'rest')}
              </h1>
            </div>
          </div>
          <p className="mt-sr-only" role="status">
            {announcement}
          </p>
          <div className="mt-guide-rest__adjust">
            <button type="button" onClick={() => shift(onMinus, -15)} aria-label={session(t, 'minus15')}>
              {session(t, 'minus15Short')}
            </button>
            <button type="button" onClick={() => shift(onPlus, 15)} aria-label={session(t, 'plus15')}>
              {session(t, 'plus15Short')}
            </button>
            {isDesktop && (
              <button type="button" className="mt-guide-rest__skip" onClick={onSkip}>
                {session(t, 'skipRest')}
              </button>
            )}
          </div>
        </div>
        <div className="mt-guide-rest__next">
          <button
            type="button"
            className="mt-guide-next"
            onClick={() => onHowTo(next.entry.index)}
            aria-label={`${next.entry.name}. ${sub}. ${session(t, 'howTo')}`}
          >
            <ExerciseArt key={next.entry.index} entry={next.entry} still={!isDesktop} className="mt-guide-next__art" />
            <span className="mt-guide-next__text">
              <span className="mt-guide-next__name">
                <EntryName entry={next.entry} />
              </span>
              <span className="mt-guide-next__sub">{sub}</span>
            </span>
            <ChevronRight className="mt-guide-next__chevron" aria-hidden="true" />
          </button>
        </div>
      </section>
      {!isDesktop && (
        <div className="mt-guide-dock mt-guide-dock--rest">
          <button type="button" className="mt-btn mt-btn--quiet mt-guide-skip" onClick={onSkip}>
            <SkipForward aria-hidden="true" />
            {session(t, 'skipRest')}
          </button>
        </div>
      )}
    </>
  );
};

export const SessionActive = ({
  workoutTitle,
  doneSets,
  totalSets,
  segments,
  entries,
  phase,
  current,
  after,
  next,
  upNext,
  restTimeLeft,
  restTotal,
  completeLabel,
  isMuted,
  voiceLevel,
  isPt,
  onClose,
  onToggleMute,
  onVoiceLevelChange,
  onRepsChange,
  onWeightChange,
  onWeightCommit,
  onStep,
  onCompleteSet,
  onMinus,
  onPlus,
  onSkip,
}) => {
  const { t } = useTranslation();
  const isDesktop = useMediaQuery({ query: DESKTOP });
  const [isSoundHelpOpen, setIsSoundHelpOpen] = useState(false);
  const [howToEntry, setHowToEntry] = useState(null);
  const headingRef = useRef(null);
  const restRef = useRef(null);
  const { entry, setNumber, reps, weight } = current;
  const setKey = `${phase}-${entry.index}-${setNumber}`;

  // Each set and each rest starts at the top. The button that was pressed is gone by then: the focus moves to the new
  // screen's heading (the rest's region) instead of falling back to the page
  useEffect(() => {
    window.scrollTo(0, 0);
    const active = document.activeElement;
    if (!active || active === document.body) (phase === 'rest' ? restRef : headingRef).current?.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setKey]);

  const openHowTo = (index) => setHowToEntry(entries[index] || null);
  const hasSteps = entry.steps.length > 0;
  const load = formatNumber(weight, isPt);
  const unit = entry.unit;
  const dots = Array.from({ length: entry.totalSets }, (_, i) => (i + 1 < setNumber ? 'done' : i + 1 === setNumber ? 'current' : 'todo'));

  return (
    <div className="mt-guide mt-guide--active">
      <header className="mt-guide-bar">
        <button type="button" className="mt-guide-icon" onClick={onClose} aria-label={session(t, 'close')}>
          <X aria-hidden="true" />
        </button>
        <div className="mt-guide-bar__mid">
          <p className="mt-guide-bar__title">{workoutTitle}</p>
          <p className="mt-guide-bar__count">{session(t, 'progress', { done: doneSets, total: totalSets })}</p>
        </div>
        <div className="mt-guide-bar__actions">
          <button type="button" className="mt-guide-icon" onClick={() => setIsSoundHelpOpen(true)} aria-label={session(t, 'soundHelp')}>
            <CircleHelp aria-hidden="true" />
          </button>
          <VoiceVolume level={voiceLevel} isMuted={isMuted} onLevelChange={onVoiceLevelChange} onToggleMute={onToggleMute} />
        </div>
        {/* The workout as segments, one per exercise, each as long as its sets */}
        <div
          className="mt-guide-segs"
          role="progressbar"
          aria-label={t('pages.WorkoutExecution.workoutProgress')}
          aria-valuemin={0}
          aria-valuemax={totalSets}
          aria-valuenow={doneSets}
          aria-valuetext={session(t, 'progress', { done: doneSets, total: totalSets })}
        >
          {segments.map((segment) => (
            <span key={segment.index} className="mt-guide-seg" style={{ '--sets': segment.sets }}>
              <span style={{ width: `${(segment.done / segment.sets) * 100}%` }} />
            </span>
          ))}
        </div>
      </header>

      {phase === 'rest' ? (
        <div className="mt-guide-stage mt-guide-stage--rest">
          <RestStage
            timeLeft={restTimeLeft}
            total={restTotal}
            next={next}
            isDesktop={isDesktop}
            onMinus={onMinus}
            onPlus={onPlus}
            onSkip={onSkip}
            onHowTo={openHowTo}
            regionRef={restRef}
          />
        </div>
      ) : (
        <div className="mt-guide-stage">
          {hasSteps ? (
            <button
              type="button"
              className="mt-guide-media"
              onClick={() => openHowTo(entry.index)}
              aria-label={session(t, 'howToFor', { name: entry.name })}
            >
              <ExerciseArt key={entry.index} entry={entry} />
              <span className="mt-guide-media__pill" aria-hidden="true">
                <Info />
                {session(t, 'howTo')}
              </span>
            </button>
          ) : (
            <div className="mt-guide-media">
              <ExerciseArt key={entry.index} entry={entry} />
            </div>
          )}
          <div className="mt-guide-side">
            <h1 className="mt-guide-name" tabIndex={-1} ref={headingRef}>
              <EntryName entry={entry} />
            </h1>
            <p className="mt-guide-meta">
              {session(t, 'exerciseOf', { index: entry.index + 1, count: entries.length })}
              {entry.muscle ? ` · ${entry.muscle}` : ''}
            </p>
            <div className="mt-guide-setline">
              <p className="mt-guide-setline__label">
                {session(t, 'setNumber', { n: setNumber })} <span>{session(t, 'setOfTotal', { total: entry.totalSets })}</span>
              </p>
              <span className="mt-guide-dots" aria-hidden="true">
                {dots.map((state, i) => (
                  <i key={i} className={`mt-guide-dots__dot mt-guide-dots__dot--${state}`} />
                ))}
              </span>
            </div>
            <div className="mt-guide-setcard" role="group" aria-label={session(t, 'setGroup', { n: setNumber, total: entry.totalSets })}>
              <SetStepper
                value={reps}
                label={session(t, 'repsLabel')}
                fieldLabel={session(t, 'repsField', { n: setNumber })}
                lessLabel={session(t, 'lessReps')}
                moreLabel={session(t, 'moreReps')}
                inputMode="numeric"
                onChange={onRepsChange}
                onCommit={() => {}}
                onStep={(direction) => onStep('reps', direction)}
              />
              <SetStepper
                value={load}
                unit={unit}
                label={session(t, 'loadLabel')}
                fieldLabel={session(t, 'loadField', { n: setNumber, unit })}
                lessLabel={session(t, 'lessLoad', { step: formatNumber(entry.loadStep, isPt), unit })}
                moreLabel={session(t, 'moreLoad', { step: formatNumber(entry.loadStep, isPt), unit })}
                inputMode="decimal"
                onChange={onWeightChange}
                onCommit={onWeightCommit}
                onStep={(direction) => onStep('weight', direction)}
              />
            </div>
            <div className="mt-guide-dock mt-guide-dock--set">
              <AfterLine after={after} />
              <button type="button" className="mt-btn mt-btn--primary mt-guide-cta" onClick={onCompleteSet}>
                <Check aria-hidden="true" />
                {completeLabel}
              </button>
            </div>
            {isDesktop && upNext.length > 0 && (
              <section className="mt-guide-upnext" aria-labelledby="mt-guide-upnext-title">
                <h2 id="mt-guide-upnext-title" className="mt-guide-upnext__title">
                  {session(t, 'upNext')}
                </h2>
                <ul>
                  {upNext.map((item) => (
                    <li key={item.index}>
                      <button type="button" className="mt-guide-upnext__row" onClick={() => openHowTo(item.index)}>
                        <ExerciseArt entry={item} still className="mt-guide-upnext__art" />
                        <span className="mt-guide-upnext__text">
                          <span className="mt-guide-upnext__name">
                            <EntryName entry={item} />
                          </span>
                          <span className="mt-guide-upnext__dose">{item.dose}</span>
                        </span>
                        <ChevronRight aria-hidden="true" />
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        </div>
      )}
      <SoundHelpDialog isOpen={isSoundHelpOpen} onClose={() => setIsSoundHelpOpen(false)} />
      <HowToSheet entry={howToEntry} onClose={() => setHowToEntry(null)} />
    </div>
  );
};

// Where the log stands once the workout is over: saving, saved (and what today's loads become), the Pro offer for free
// accounts (the history is Pro), or a failure with a retry
const LogStatus = ({ logStatus, workoutTitle, onRetry, onUpgrade }) => {
  const { t } = useTranslation();
  const loads = session(t, 'loadsCarry', { workout: workoutTitle });

  if (logStatus === 'saving') {
    return (
      <p className="mt-guide-done__line">
        <Clock aria-hidden="true" />
        <span>{session(t, 'saving')}</span>
      </p>
    );
  }
  if (logStatus === 'saved') {
    return (
      <p className="mt-guide-done__line">
        <Check className="mt-guide-done__ok" aria-hidden="true" />
        <span>
          <b>{session(t, 'saved')}</b> {loads}
        </span>
      </p>
    );
  }
  if (logStatus === 'pro') {
    return (
      <div className="mt-guide-done__offer">
        <p className="mt-guide-done__line">
          <TrendingUp aria-hidden="true" />
          <span>
            <b>{session(t, 'proLog')}</b> {loads}
          </span>
        </p>
        <button type="button" className="mt-text-btn" onClick={onUpgrade}>
          {session(t, 'proLogCta')}
          <ChevronRight aria-hidden="true" />
        </button>
      </div>
    );
  }
  if (logStatus === 'error') {
    return (
      <div className="mt-guide-done__offer">
        <p className="mt-guide-done__line">
          <X aria-hidden="true" />
          <span>{session(t, 'logError')}</span>
        </p>
        <button type="button" className="mt-text-btn" onClick={onRetry}>
          {session(t, 'retry')}
        </button>
      </div>
    );
  }
  return null;
};

export const SessionComplete = ({
  workoutTitle,
  description,
  minutes,
  setCount,
  exerciseCount,
  logStatus,
  records = [],
  isPt,
  onRetry,
  onUpgrade,
  onViewProgress,
  onBack,
}) => {
  const { t } = useTranslation();
  const dose = (set) => `${formatNumber(set.weight, isPt)} ${set.unit}`;
  const headingRef = useRef(null);

  // The finish button is gone: the focus lands on the title
  useEffect(() => {
    if (document.activeElement === document.body) headingRef.current?.focus({ preventScroll: true });
  }, []);

  return (
    <div className="mt-guide mt-guide--done">
      <div className="mt-guide-done">
        <span className="mt-guide-done__badge" aria-hidden="true">
          {records.length > 0 ? <Trophy /> : <Check />}
          {records.length > 0 && <Burst />}
        </span>
        <h1 className="mt-guide-done__title" tabIndex={-1} ref={headingRef}>
          {session(t, 'doneTitle')}
        </h1>
        <p className="mt-guide-done__sub">{[workoutTitle, description].filter(Boolean).join(' · ')}</p>
        <dl className="mt-guide-stats mt-guide-done__stats">
          <div>
            <dt>{session(t, 'statDuration')}</dt>
            <dd>
              {minutes}
              <small> {session(t, 'minutesUnit')}</small>
            </dd>
          </div>
          <div>
            <dt>{session(t, 'statSets', { count: setCount })}</dt>
            <dd>{setCount}</dd>
          </div>
          <div>
            <dt>{session(t, 'statExercises', { count: exerciseCount })}</dt>
            <dd>{exerciseCount}</dd>
          </div>
        </dl>
        {records.length > 0 && (
          <section className="mt-guide-records" aria-labelledby="mt-guide-records-title">
            <h2 id="mt-guide-records-title" className="mt-guide-records__title">
              {session(t, 'recordsTitle', { count: records.length })}
            </h2>
            <ul className="mt-guide-records__list">
              {records.map((record) => (
                <li key={record.name}>
                  <Trophy aria-hidden="true" />
                  <span>
                    <span className="mt-guide-records__name">{record.name}</span>
                    <span className="mt-guide-records__detail">
                      {record.kind === 'weight'
                        ? session(t, 'recordWeight', {
                            value: dose(record.set),
                            previous: formatNumber(Math.round(record.previousKg * 10) / 10, isPt),
                          })
                        : session(t, 'recordBestSet', { value: dose(record.set), reps: record.set.reps })}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}
        <div className="mt-guide-done__status" role="status">
          <LogStatus logStatus={logStatus} workoutTitle={workoutTitle} onRetry={onRetry} onUpgrade={onUpgrade} />
        </div>
        <div className="mt-guide-dock mt-guide-dock--done">
          <button type="button" className="mt-btn mt-btn--primary mt-guide-cta" onClick={onBack}>
            {session(t, 'backToWorkouts')}
          </button>
          <button type="button" className="mt-btn mt-btn--quiet mt-guide-quiet" onClick={onViewProgress} disabled={logStatus === 'saving'}>
            <TrendingUp aria-hidden="true" />
            {session(t, 'viewProgress')}
          </button>
        </div>
      </div>
    </div>
  );
};

export const LeaveSessionDialog = ({ isOpen, onStay, onLeave }) => {
  const { t } = useTranslation();

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => !open && onStay()}
      placement="center"
      hideCloseButton
      classNames={{ base: 'mt-root mt-dialog', backdrop: 'mt-dialog__backdrop' }}
    >
      <ModalContent>
        {() => (
          <div className="mt-dialog__body">
            <h2 className="mt-dialog__title">{session(t, 'leaveTitle')}</h2>
            <p className="mt-dialog__text">{session(t, 'leaveBody')}</p>
            <div className="mt-dialog__actions">
              <button type="button" className="mt-btn mt-btn--quiet" onClick={onLeave}>
                {session(t, 'leave')}
              </button>
              <button type="button" className="mt-btn mt-btn--primary" onClick={onStay}>
                {session(t, 'stay')}
              </button>
            </div>
          </div>
        )}
      </ModalContent>
    </Modal>
  );
};
